import React, { useState } from 'react';
import PageShell, { PageHeader, Flourish } from '../common/PageShell';
import { colors, layout, motion, space, type } from '../../styles/tokens';

const roles = [
  {
    role: 'Faculty Data Tool Developer',
    date: 'Fall 2026 – Present',
    org: 'Yale School of the Environment',
    mode: 'Part-time',
    body: (
      <>
        Hired after YSE reviewed the faculty-reporting platform I built for the School of
        Management. Adapting that product to <em>YSE's own faculty-data workflows</em> and scoping
        where else internally deployed software and AI tools would actually be worth building for
        the school.
      </>
    )
  },
  {
    role: 'AI Research Intern',
    date: 'Summer 2026',
    org: "Yale School of Management, Dean's Office",
    mode: 'Full-time',
    body: (
      <>
        Spearheaded two internal data and AI products, independently owning each from{' '}
        <em>product scoping and design through engineering, deployment, and rollout</em>. Built a
        faculty research-impact platform that matches faculty to Google Scholar profiles, aggregates
        publication and citation metrics, flags profile issues, and generates reports for
        school-wide analysis. Also designed and built an <em>AI curriculum-mapping platform</em>{' '}
        that analyzes up to 80 syllabi at once and returns evidence-linked answers showing where
        themes are taught across courses — approved internally and now in Yale Data Governance
        review ahead of broader faculty use.
      </>
    )
  },
  {
    role: 'Software Developer',
    date: 'Fall 2025 – Present',
    org: 'CourseTable [Yale Computer Society]',
    mode: 'Part-time',
    body: (
      <>
        Selected as <em>1 of 6 developers</em> through Yale Computer Society's competitive team
        hiring process to work on Yale's course discovery and scheduling platform, used by over{' '}
        <em>6,000 students</em>. Shipped 10+ user-facing improvements, including{' '}
        <em>fuzzy and semantic search</em> so students can find relevant courses without guessing
        exact keywords, plus scheduling improvements that make it easier to evaluate classes across
        the timeframes that fit them. Also contribute bug fixes and quality-of-life updates while
        collaborating in a team-based engineering environment.
      </>
    )
  },
  {
    role: 'IT Intern',
    date: 'Spring 2024 – Spring 2025',
    org: 'Culver City City Hall',
    mode: 'In-Person',
    body: (
      <>
        Supported day-to-day IT operations by <em>troubleshooting hardware and software issues</em>{' '}
        for staff and helping maintain the reliability of city systems. Also used Power BI and Excel
        to clean and organize homeless encampment data for a new dashboard, contributing to tools
        that supported internal decision-making and public services.
      </>
    )
  },
  {
    role: 'Math Instructor',
    date: 'Fall 2023 – Spring 2025',
    org: 'Mathnasium',
    mode: 'In-Person',
    body: (
      <>
        Taught math concepts to students ranging from elementary through high school in a
        small-group setting, adapting instruction to different learning styles and helping build
        confidence in problem solving.
      </>
    )
  }
];

const awards = [
  { name: 'Tsai CITY Launchpad', note: 'Selected, Spring 2026' },
  {
    name: 'Y Combinator Startup School',
    note: 'Participant, Summer 2026',
    href: '/yc-startup-school-2026.jpg'
  },
  { name: 'Congressional App Challenge', note: '3rd Place, California Region 36' },
  {
    name: 'E-Cubed · Founder',
    note: 'Founded first SHPE chapter; raised $2K; organized Google/JPL visits'
  }
];

const AwardRow = ({ name, note, href }) => {
  const [hovered, setHovered] = useState(false);

  const nameStyle = { ...type.body, fontWeight: 500, color: colors.inkStrong };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: space[3],
        flexWrap: 'wrap',
        padding: `${space[2]} 0`,
        borderBottom: `1px solid ${colors.hairline}`
      }}
    >
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            ...nameStyle,
            textDecoration: 'none',
            color: hovered ? colors.sageDeep : colors.inkStrong,
            transition: `color ${motion.fast} ease`
          }}
        >
          {name}
          <span
            style={{
              ...type.link,
              fontSize: '13px',
              marginLeft: space[1],
              display: 'inline-block',
              transition: `transform ${motion.fast} ease`,
              transform: hovered ? 'translateX(3px)' : 'translateX(0)'
            }}
          >
            ticket →
          </span>
        </a>
      ) : (
        <span style={nameStyle}>{name}</span>
      )}
      <span style={{ ...type.meta, fontStyle: 'italic', textAlign: 'right' }}>{note}</span>
    </div>
  );
};

const ExperiencePage = () => (
  <PageShell>
    <PageHeader title="Experience" subtitle="Professional journey" flourish="◈" />

    {roles.map((r) => (
      <div
        key={r.role + r.org}
        style={{
          marginBottom: space[5],
          paddingLeft: space[3],
          borderLeft: `1px solid ${colors.hairlineStrong}`
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: space[2],
            marginBottom: space[1],
            flexWrap: 'wrap'
          }}
        >
          <h3 style={type.h3}>{r.role}</h3>
          <span style={{ ...type.meta, fontStyle: 'italic' }}>{r.date}</span>
        </div>

        <p style={{ ...type.meta, fontSize: '14px', color: colors.sageDeep, marginBottom: space[2] }}>
          {r.org}
          <Flourish tone={colors.sageLight}>•</Flourish>
          {r.mode}
        </p>

        <p style={{ ...type.body, maxWidth: layout.prose }}>{r.body}</p>
      </div>
    ))}

    <h2 style={{ ...type.h2, margin: `${space[7]} 0 ${space[3]}` }}>
      Leadership &amp; Awards
      <Flourish tone={colors.sageLight}>※</Flourish>
    </h2>

    {awards.map((award) => (
      <AwardRow key={award.name} {...award} />
    ))}
  </PageShell>
);

export default ExperiencePage;
